import React, { useState } from 'react'
import { ChargingSessionStatus, MIDMeterFilterValue } from '../../types'
import { SvgIcon, Stack, Button, Typography, IconButton } from '@mui/material'
import { ReactComponent as FilterIcon } from '../icons/filter_icon.svg'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ChargingSessionsMobileFiltersProps, AccordionFilterItem } from '../../models/charging-sessions.model'
import { ChargingSessionMobileFilterItem } from './ChargingSessionMobileFilterItem'
import { MobileDrawer } from 'src/Elements/Drawer'
import { J2Multiply } from '@e3dc-react/icons'
import { isEqual } from 'lodash'
import { useTypedSelector } from 'src/State/RootReducer'
import { useDispatch } from 'react-redux'
import { updateChargingSessionsFilters } from 'src/State/E-Mobility/EMobilityStateAction'
import { useChargingSessionsStatus } from '../../hooks/use-charging-sessions-status'

const useStyles = makeStyles()(theme => ({
  filterButtonIcon: {
    height: theme.shape.filter.height,
    width: theme.shape.filter.height,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(1),
    minWidth: 'unset',
  },
  drawerTitle: {
    fontSize: '24px',
    fontWeight: 400,
    lineHight: 'normal',
  },
}))

/**
 * Component which button for filtering charing session on mobile screens.
 * It displays and hold as container, button for opening mobile drawer with all other filter components.
 */
export const ChargingSessionsMobileFilters: React.FC<ChargingSessionsMobileFiltersProps> = ({
  wallboxOptions,
  authenticationOptions,
  shouldDisplayMidMeter,
  sessionStatusFilterOptions,
}) => {
  const chargingSessionFilters = useTypedSelector(state => state.eMobility.chargingSessionsFilters.filters)

  const sessionStatuses = chargingSessionFilters?.statuses ?? []
  const wallboxIds = chargingSessionFilters?.wallboxIds ?? []
  const midMeter = chargingSessionFilters?.midMeter ?? []
  const ocpiTokenIds = chargingSessionFilters?.ocpiTokenIds ?? []

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedWallboxes, setSelectedWallboxes] = useState<string[]>(wallboxIds)
  const [selectedChargingSessions, setSelectedChargingSessions] = useState<ChargingSessionStatus[]>(sessionStatuses)
  const [selectedAuthentication, setSelectedAuthentication] = useState<string[]>(ocpiTokenIds)
  const [selectedMidFilter, setSelectedMidFilter] = useState<MIDMeterFilterValue[]>(midMeter)
  const [accordionFilterItem, setAccordionFilterItem] = useState<AccordionFilterItem>(false)

  const { t } = useCustomTranslation()
  const { classes } = useStyles()

  const dispatch = useDispatch()

  const formattedChargingSessionStatusOptions = useChargingSessionsStatus(sessionStatusFilterOptions)

  const midMeterOptions = [
    { value: MIDMeterFilterValue.withMIDMeter, label: t(`emobility.chargingSession.${MIDMeterFilterValue.withMIDMeter}`) },
    { value: MIDMeterFilterValue.withoutMIDMeter, label: t(`emobility.chargingSession.${MIDMeterFilterValue.withoutMIDMeter}`) },
  ]

  const hasFilterSelected =
    selectedWallboxes.length > 0 || selectedChargingSessions.length > 0 || selectedAuthentication.length > 0 || selectedMidFilter.length > 0

  const shouldDisableApplyButton =
    isEqual(selectedWallboxes, wallboxIds) &&
    isEqual(selectedAuthentication, ocpiTokenIds) &&
    isEqual(selectedChargingSessions, sessionStatuses) &&
    isEqual(selectedMidFilter, midMeter)

  const handleDrawerOpen = (): void => {
    setSelectedWallboxes(wallboxIds)
    setSelectedChargingSessions(sessionStatuses)
    setSelectedAuthentication(ocpiTokenIds)
    setSelectedMidFilter(midMeter)

    setIsDrawerOpen(true)
  }

  const handleDrawerClose = (): void => {
    setAccordionFilterItem(false)
    setIsDrawerOpen(false)
  }

  const handleApplyFilter = (): void => {
    dispatch(
      updateChargingSessionsFilters({
        wallboxIds: selectedWallboxes,
        ocpiTokenIds: selectedAuthentication,
        statuses: selectedChargingSessions,
        midMeter: selectedMidFilter,
        page: 0,
        limit: chargingSessionFilters?.limit,
      })
    )
    setAccordionFilterItem(false)
    handleDrawerClose()
  }

  const handleClearAllFilter = (): void => {
    setSelectedAuthentication([])
    setSelectedChargingSessions([])
    setSelectedWallboxes([])
    setSelectedMidFilter([])

    dispatch(
      updateChargingSessionsFilters({
        wallboxIds: [],
        ocpiTokenIds: [],
        midMeter: [],
        statuses: [],
        page: 0,
        limit: chargingSessionFilters?.limit,
      })
    )

    setAccordionFilterItem(false)
    handleDrawerClose()
  }

  return (
    <>
      <Button
        variant={hasFilterSelected ? 'contained' : 'outlined'}
        id="charging-session-filter-button"
        className={classes.filterButtonIcon}
        onClick={handleDrawerOpen}
        disableElevation
      >
        <SvgIcon component={FilterIcon} sx={{ color: hasFilterSelected ? 'common.white' : 'primary.main', fontSize: 30 }} inheritViewBox />
      </Button>

      <MobileDrawer onClose={handleDrawerClose} open={isDrawerOpen}>
        <Stack flexDirection="row" justifyContent="space-between" alignItems="center" pb={2}>
          <Typography className={classes.drawerTitle}>{t('emobility.chargingSession.filters')}</Typography>
          <IconButton onClick={handleDrawerClose} size="large" color="inherit">
            <J2Multiply fontSize="inherit" />
          </IconButton>
        </Stack>

        <Stack direction="column" justifyContent="space-between" height="100%" overflow="scroll" pb={8}>
          <Stack>
            <ChargingSessionMobileFilterItem
              filterOptionLabel={t('emobility.chargingSession.wallbox')}
              filterOptions={wallboxOptions}
              selectedFilterOptions={selectedWallboxes}
              setSelectedFilters={setSelectedWallboxes}
              accordionFilterItemValue={'accordionFilterItem1'}
              setAccordionFilterItem={setAccordionFilterItem}
              selectedAccordionFilterItem={accordionFilterItem}
            />
            <ChargingSessionMobileFilterItem
              filterOptionLabel={t('emobility.chargingSession.status')}
              filterOptions={formattedChargingSessionStatusOptions}
              selectedFilterOptions={selectedChargingSessions}
              setSelectedFilters={setSelectedChargingSessions}
              accordionFilterItemValue={'accordionFilterItem2'}
              setAccordionFilterItem={setAccordionFilterItem}
              selectedAccordionFilterItem={accordionFilterItem}
            />
            <ChargingSessionMobileFilterItem
              filterOptionLabel={t('emobility.chargingSession.authentication')}
              filterOptions={authenticationOptions}
              selectedFilterOptions={selectedAuthentication}
              setSelectedFilters={setSelectedAuthentication}
              accordionFilterItemValue={'accordionFilterItem3'}
              setAccordionFilterItem={setAccordionFilterItem}
              selectedAccordionFilterItem={accordionFilterItem}
            />
            {shouldDisplayMidMeter && (
              <ChargingSessionMobileFilterItem
                filterOptionLabel={t('emobility.chargingSession.mid-status')}
                filterOptions={midMeterOptions}
                selectedFilterOptions={selectedMidFilter}
                setSelectedFilters={setSelectedMidFilter}
                accordionFilterItemValue={'accordionFilterItem4'}
                setAccordionFilterItem={setAccordionFilterItem}
                selectedAccordionFilterItem={accordionFilterItem}
              />
            )}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={theme => ({
              width: '100%',
              backgroundColor: 'background.paper',
              py: 2,
              mx: -2,
              px: 4,
              position: 'fixed',
              bottom: 0,
              borderTop: `1px solid ${theme.palette.grey.grey06}`,
            })}
          >
            <Button fullWidth variant="outlined" color="secondary" onClick={handleClearAllFilter} disabled={!hasFilterSelected}>
              {t('filter.clearAllButtonText')}
            </Button>
            <Button fullWidth variant="contained" onClick={handleApplyFilter} disabled={shouldDisableApplyButton}>
              {t('common.apply')}
            </Button>
          </Stack>
        </Stack>
      </MobileDrawer>
    </>
  )
}
